import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { useAirQualityStore } from '@/stores/useAirQualityStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { useEnvironmentalSampling } from '@/hooks/useEnvironmentalSampling';
import { VideoOverlayScreen } from './VideoOverlayScreen';
import type { RecordingResult } from '@/types/running';

interface LiveRecordingScreenProps {
  isOpen: boolean;
  onFinish: (result: RecordingResult) => void;
  onDiscard: () => void;
}

const STATIONARY_TYPES = new Set(['yoga', 'strength', 'treadmill', 'swim']);

export function LiveRecordingScreen({ isOpen, onFinish, onDiscard }: LiveRecordingScreenProps) {
  const {
    isRecording, recordingData, stopRecording,
    pauseRecording, resumeRecording, captureSnapshot,
  } = useRunningStore();
  const airQuality = useAirQualityStore((s) => s.currentReading);

  const [elapsed, setElapsed] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);
  const [gpsStatus, setGpsStatus] = useState<'waiting' | 'active' | 'denied' | 'error'>('waiting');
  const lastPositionRef = useRef<{ lat: number; lng: number; timestamp: number } | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isStationary = STATIONARY_TYPES.has(recordingData.activityType);

  // Environmental sampling hook
  useEnvironmentalSampling(isRecording);

  // Timer
  useEffect(() => {
    if (!isRecording || !isOpen) return;

    timerRef.current = setInterval(() => {
      const { recordingData: rd } = useRunningStore.getState();
      if (!rd.startTime || rd.isPaused) return;
      const totalElapsed = Date.now() - rd.startTime;
      const active = Math.floor((totalElapsed - rd.pausedDuration) / 1000);
      setElapsed(active);
    }, 200);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording, isOpen]);

  // GPS tracking - use location data from the map's GPS watch
  useEffect(() => {
    if (!isRecording || !isOpen || isStationary) return;

    console.log('[GPS] Starting GPS tracking from location store...');
    console.log('[GPS] Initial route points:', recordingData.route.length);

    // Poll location store for position updates
    const intervalId = setInterval(() => {
      const { currentPosition, timestamp } = useLocationStore.getState();

      if (!currentPosition || !timestamp) {
        setGpsStatus('waiting');
        console.log('[GPS] No position data yet, waiting...');
        return;
      }

      // Check if we have a new position (different from last recorded)
      const lastPos = lastPositionRef.current;
      if (!lastPos || lastPos.lat !== currentPosition.lat || lastPos.lng !== currentPosition.lng || timestamp !== lastPos.timestamp) {
        setGpsStatus('active');

        // Add route point
        const currentAqi = useAirQualityStore.getState().currentReading?.aqi;
        const point = {
          lat: currentPosition.lat,
          lng: currentPosition.lng,
          elevation: undefined,
          timestamp: new Date(timestamp).toISOString(),
          aqi: currentAqi || undefined,
        };

        console.log('[GPS] Adding route point from location store:', point);
        useRunningStore.getState().addRoutePoint(point);
        console.log('[GPS] Route updated - Points:', useRunningStore.getState().recordingData.route.length, 'Distance:', useRunningStore.getState().recordingData.distance);

        // Update last position
        lastPositionRef.current = {
          lat: currentPosition.lat,
          lng: currentPosition.lng,
          timestamp
        };
      }
    }, 1000); // Check every 1 second for more responsive updates

    return () => {
      clearInterval(intervalId);
      console.log('[GPS] Stopping GPS tracking');
      console.log('[GPS] Final route points:', useRunningStore.getState().recordingData.route.length);
      console.log('[GPS] Final distance:', useRunningStore.getState().recordingData.distance);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, isOpen, isStationary]);

  const handleStop = useCallback(() => {
    setShowConfirm(true);
  }, []);

  const confirmStop = useCallback(() => {
    const result = stopRecording();
    if (result) onFinish(result);
    else onDiscard();
    setShowConfirm(false);
    setElapsed(0);
  }, [stopRecording, onFinish, onDiscard]);

  const handleDiscard = useCallback(() => {
    stopRecording();
    onDiscard();
    setShowConfirm(false);
    setElapsed(0);
  }, [stopRecording, onDiscard]);

  const togglePause = useCallback(() => {
    if (recordingData.isPaused) resumeRecording();
    else pauseRecording();
  }, [recordingData.isPaused, pauseRecording, resumeRecording]);

  // Format helpers
  const formatTime = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const distKm = recordingData.distance / 1000;
  // Distance-based calorie calculation (per km, assumes ~70kg person)
  const calPerKm: Record<string, number> = {
    walk: 55, run: 70, hike: 75, trail_run: 80, cycle: 35,
  };
  const calRate = calPerKm[recordingData.activityType] || 65;
  const isDistanceActivity = ['walk', 'run', 'hike', 'trail_run', 'cycle'].includes(recordingData.activityType);
  const calories = isDistanceActivity
    ? Math.round(distKm * calRate)
    : Math.round(elapsed / 60 * 5);

  // Latest env data from last AQI sample
  const lastSample = recordingData.aqi_samples[recordingData.aqi_samples.length - 1];

  const activityLabels: Record<string, string> = {
    run: 'Running', walk: 'Walking', cycle: 'Cycling', hike: 'Hiking',
    trail_run: 'Trail Running', yoga: 'Yoga', swim: 'Swimming',
    strength: 'Strength', treadmill: 'Treadmill', other: 'Activity',
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[55] pointer-events-none"
      >
        {/* Top bar */}
        <div className="pointer-events-auto absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent pt-12 pb-6 px-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${recordingData.isPaused ? 'bg-yellow-400' : 'bg-red-500 animate-pulse'}`} />
                <span className="text-white font-semibold text-sm uppercase tracking-wider">
                  {recordingData.isPaused ? 'Paused' : activityLabels[recordingData.activityType] || 'Recording'}
                </span>
              </div>
              {!isStationary && (
                <div className="flex items-center gap-1.5 text-xs">
                  <div className={`w-2 h-2 rounded-full ${
                    gpsStatus === 'active' ? 'bg-emerald-400' :
                    gpsStatus === 'waiting' ? 'bg-yellow-400 animate-pulse' :
                    gpsStatus === 'denied' ? 'bg-red-400' : 'bg-orange-400'
                  }`} />
                  <span className="text-gray-400">
                    {gpsStatus === 'active' ? 'GPS Active' :
                     gpsStatus === 'waiting' ? 'Waiting for GPS...' :
                     gpsStatus === 'denied' ? 'GPS Denied - Enable Location' :
                     'GPS Error'}
                    {' • '}
                    {recordingData.route.length} pts • {recordingData.distance.toFixed(0)}m
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowConfirm(true)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* GPS Permission Help Banner */}
        {!isStationary && gpsStatus === 'denied' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="pointer-events-auto absolute top-32 left-4 right-4 bg-red-500/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-red-400/30"
          >
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-white mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h4 className="text-white font-bold text-sm mb-1.5">Location Access Required</h4>
                <p className="text-white/90 text-xs mb-3">
                  Distance tracking won't work without location permission. Enable it in your phone settings:
                </p>
                <div className="text-white/90 text-xs space-y-1.5">
                  <div className="font-semibold">iPhone/iPad:</div>
                  <div className="pl-3">Settings → Safari → Location → While Using</div>
                  <div className="font-semibold mt-2">Android:</div>
                  <div className="pl-3">Settings → Apps → Browser → Permissions → Location → Allow</div>
                </div>
                <p className="text-white/75 text-xs mt-3 italic">
                  After enabling, reload the page and start a new recording.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Bottom stats panel */}
        <div className="pointer-events-auto absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0d1117] via-[#0d1117]/95 to-transparent pt-12 pb-8 px-6">
          {/* Timer */}
          <div className="text-center mb-6">
            <span className="text-5xl font-mono font-bold text-white tracking-wider">
              {formatTime(elapsed)}
            </span>
          </div>

          {/* Main stats */}
          {!isStationary && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white/5 rounded-2xl p-3 text-center border border-white/5">
                <div className="text-2xl font-bold text-white">{distKm.toFixed(2)}</div>
                <div className="text-xs text-gray-400 mt-1">km</div>
              </div>
              <div className="bg-white/5 rounded-2xl p-3 text-center border border-white/5">
                <div className="text-2xl font-bold text-white">{calories}</div>
                <div className="text-xs text-gray-400 mt-1">cal</div>
              </div>
            </div>
          )}

          {/* Compact environmental bar */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
              (airQuality?.aqi || 0) <= 50 ? 'bg-emerald-500/15 text-emerald-400' :
              (airQuality?.aqi || 0) <= 100 ? 'bg-yellow-500/15 text-yellow-400' : 'bg-red-500/15 text-red-400'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${
                (airQuality?.aqi || 0) <= 50 ? 'bg-emerald-400' :
                (airQuality?.aqi || 0) <= 100 ? 'bg-yellow-400' : 'bg-red-400'
              }`} />
              AQI {airQuality?.aqi || '--'}
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 text-xs text-gray-400">
              {lastSample?.temperature !== null && lastSample?.temperature !== undefined
                ? `${Math.round(lastSample.temperature)}\u00B0C`
                : '--'}
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 text-xs text-gray-400">
              {lastSample?.humidity !== null && lastSample?.humidity !== undefined
                ? `${Math.round(lastSample.humidity)}%`
                : '--'}
              <svg className="w-3 h-3 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2C20 10.48 17.33 6.55 12 2z" />
              </svg>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-4 justify-center">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={togglePause}
              className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center"
            >
              {recordingData.isPaused ? (
                <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              ) : (
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              )}
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleStop}
              className="w-20 h-20 rounded-full bg-red-500 hover:bg-red-400 flex items-center justify-center shadow-lg shadow-red-500/30"
            >
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </motion.button>

            {/* Snapshot button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={captureSnapshot}
              className="w-16 h-16 rounded-full bg-cyan-500/20 border border-cyan-400/50 flex items-center justify-center"
            >
              <svg className="w-7 h-7 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </motion.button>

            {/* Video overlay camera button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowVideoOverlay(true)}
              className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center"
            >
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </motion.button>
          </div>
        </div>

        {/* Video Overlay Screen */}
        <VideoOverlayScreen
          isOpen={showVideoOverlay}
          onClose={() => setShowVideoOverlay(false)}
          elapsed={elapsed}
        />

        {/* Confirmation dialog */}
        <AnimatePresence>
          {showConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="pointer-events-auto fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center px-6"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-[#161b22] border border-white/10 rounded-2xl p-6 w-full max-w-sm"
              >
                <h3 className="text-lg font-bold text-white mb-2">Finish Activity?</h3>
                <p className="text-gray-400 text-sm mb-6">
                  {distKm > 0.01 ? `${distKm.toFixed(2)} km in ${formatTime(elapsed)}` : formatTime(elapsed)}
                </p>
                <div className="space-y-3">
                  <button
                    onClick={confirmStop}
                    className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 rounded-xl font-bold text-black transition-colors"
                  >
                    Save Activity
                  </button>
                  <button
                    onClick={handleDiscard}
                    className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    Discard
                  </button>
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="w-full py-3 text-gray-500 hover:text-gray-300 text-sm transition-colors"
                  >
                    Continue Recording
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
